import React from "react";
import DropdownMenu from "@imminently/decisively-ui/DropdownMenu";
import Button from "@imminently/decisively-ui/Button";
import Icon from "@imminently/decisively-ui/Icon";
import Avatar from "@imminently/decisively-ui/Avatar";
import Tooltip from "@imminently/decisively-ui/Tooltip";
import { cn } from "@/lib/utils";
import Chip, { type ChipVariantKey } from "@imminently/decisively-ui/Chip";

export interface CardAction {
  name: string;
  onClick: () => void;
}

export interface CardUser {
  id: string;
  name: string;
}

// ─── Menu ───────────────────────────────────────────────────────────────────

const MenuTrigger = React.forwardRef<HTMLButtonElement, React.ButtonHTMLAttributes<HTMLButtonElement>>(
  (props, ref) => (
    <Button icon ref={ref as any} {...(props as any)} variant={"ghost"} label="More actions" size="md">
      <Icon name="show-more" />
    </Button>
  ),
);
MenuTrigger.displayName = "CardMenuTrigger";

export const CardMenu = ({ children }: { children: React.ReactNode }) => (
  <DropdownMenu>
    <DropdownMenu.Trigger asChild>
      <MenuTrigger />
    </DropdownMenu.Trigger>
    <DropdownMenu.Content align="end">
      {children}
    </DropdownMenu.Content>
  </DropdownMenu>
);

// ─── Status chip ─────────────────────────────────────────────────────────────

export const StatusChip = ({ status, variant }: { status: string; variant?: ChipVariantKey }) => (
  <Chip
    variant={variant ?? "subtle"}
    size="sm"
    label={<span className="capitalize">{status}</span>}
  />
);

// ─── User stack ──────────────────────────────────────────────────────────────

const MAX_VISIBLE_USERS = 5;

export const UserStack = ({ users }: { users: CardUser[] }) => {
  if (!users.length) return null;
  const visible = users.slice(0, MAX_VISIBLE_USERS);
  const overflow = users.length - MAX_VISIBLE_USERS;

  return (
    <div className="flex">
      {visible.map((u, i) => (
        <Tooltip key={u.id}>
          <Tooltip.Trigger asChild>
            <Avatar
              name={u.name}
              size="md"
              className="border-2 border-background"
              style={{ marginLeft: i === 0 ? 0 : -8, zIndex: i }}
            />
          </Tooltip.Trigger>
          <Tooltip.Content>{u.name}</Tooltip.Content>
        </Tooltip>
      ))}
      {overflow > 0 && (
        <Avatar
          invert
          size="md"
          className="border-2 border-background"
          style={{ marginLeft: -8, zIndex: MAX_VISIBLE_USERS }}
        >
          +{overflow}
        </Avatar>
      )}
    </div>
  );
};

// ─── Menu visibility wrapper ─────────────────────────────────────────────────

export const MenuSlot = ({ children, className }: { children: React.ReactNode; className?: string }) => (
  <div
    className={cn(
      "shrink-0 opacity-0 transition-opacity",
      "group-hover/card:opacity-100 has-data-[state=open]:opacity-100",
      className,
    )}
  >
    {children}
  </div>
);
